import { useFundraising } from "@/contexts/FunderContext";
import { useSubscriptionPurchase } from "@/contexts/SubscriptionPurchaseContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoChatConversation } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatMessage, Metadata } from "@/models/ai-chat/LazoChatMessage";
import { AddOnObjectKeyType, getAddOnMessage } from "@/models/subscriptions/AddOn";
import FunderListService from "@/services/FunderListService";
import { Funder, FunderList, FunderType } from "@/types/FunderTypes";
import { FileDownload } from "@mui/icons-material";
import { Box, Button, Checkbox, Divider, Typography, styled } from "@mui/material";
import React, { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FunderImage } from "../fundraising/FunderImage";
import CommonButton from "../utils/CommonButton";
import { InputField } from "../utils/InputField";
import LazoModal from "../utils/LazoModal";
import LazoTooltip from "../utils/LazoTooltip";
interface Props {
  lazoChatMessage?: LazoChatMessage;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  lazoChatConversation?: LazoChatConversation;
}

export interface MessageFunder {
  name: string;
  type: FunderType;
  uuid: string;
}

const AddInvestorToFunderListModal: FC<Props> = ({ lazoChatMessage, open, lazoChatConversation, setOpen }) => {
  const navigate = useNavigate();
  const params = useParams();
  const { setCurrentFunderListUpdatedTime } = useFundraising();

  const [funderLists, setFunderLists] = useState<FunderList[]>([]);

  const [selectedFunders, setSelectedFunders] = useState<Funder[]>([]);
  const [selectedFunderListsExternalIds, setSelectedFunderListsExternalIds] = useState<string[]>([]);
  const [funders, setFunders] = useState<Funder[]>([]);
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const [showNameInput, setShowNameInput] = useState(false);
  const { hasSmartListsCreation } = useSubscriptionPurchase();

  const funderListNameRef = useRef(null);

  useEffect(() => {
    let message: Metadata;

    if (typeof lazoChatMessage.metadata === "string") {
      message = JSON.parse(lazoChatMessage.metadata);
    } else {
      message = lazoChatMessage.metadata;
    }

    if (message.investors) {
      const investors: MessageFunder[] = message.investors as MessageFunder[];
      setFunders(
        //@ts-ignore
        investors.map((funder) => {
          return {
            ...funder,
            uuid: funder.uuid,
            type: funder.type.toUpperCase() as FunderType,
            entity: {
              name: funder.name,
            },
          };
        })
      );

      if (investors.length == 1) {
        setSelectedFunders(
          //@ts-ignore
          investors.map((funder) => {
            return {
              ...funder,
              type: funder.type.toUpperCase() as FunderType,
            };
          })
        );
      }
    }
  }, [lazoChatMessage, open]);

  useEffect(() => {
    getFunderLists();
    setSelectedFunders(funders);
  }, [funders]);

  const getFunderLists = () => {
    FunderListService.getFunderListsPaginated(0, 20).then((r) => {
      r.onSuccess((r) => {
        setFunderLists(r.content);
      });
      r.notifyErrorIfPresent();
    });
  };

  const onClickFunder = (funder: Funder) => {
    const index = selectedFunders.findIndex((selectedFunder) => selectedFunder.uuid === funder.uuid);

    if (index === -1) {
      setSelectedFunders((prev) => [...prev, funder]);
    } else {
      setSelectedFunders((prev) => {
        const updatedSelectedPeople = [...prev];
        updatedSelectedPeople.splice(index, 1);
        return updatedSelectedPeople;
      });
      setAllChecked(false);
    }
  };

  const onClickFunderList = (funderList: FunderList) => {
    if (!funderList.externalId) return;
    const index = selectedFunderListsExternalIds.findIndex((externalId) => externalId === funderList.externalId);

    if (index === -1) {
      setSelectedFunderListsExternalIds((prev) => [...prev, funderList.externalId]);
    } else {
      setSelectedFunderListsExternalIds((prev) => {
        const updatedFunderListsExternalId = [...prev];
        updatedFunderListsExternalId.splice(index, 1);
        return updatedFunderListsExternalId;
      });
    }
  };

  useEffect(() => {
    if (funders.length > 0) {
      if (selectedFunders.length === funders.length) setAllChecked(true);
    }
  }, [selectedFunders]);

  const onCloseModal = () => {
    setFunders([]);
    setSelectedFunderListsExternalIds([]);
    setOpen(false);
  };

  const renderButtons = () => {
    return (
      <>
        <CommonButton purpleBorderVariant onClick={onCloseModal}>
          Cancel
        </CommonButton>
        <CommonButton disabled={!hasSmartListsCreation} onClick={onSave}>
          Save
        </CommonButton>
      </>
    );
  };

  const addSelectedFunderListToMultipleList = () => {
    if (selectedFunderListsExternalIds.length == 0 || selectedFunders.length == 0) return;

    FunderListService.addFundersToMultipleLists(selectedFunderListsExternalIds, selectedFunders).then((res) => {
      res.onSuccess((r) => {
        NotificationService.info("Contact/s successfully added to your Smartlist");
        r.forEach((funderList) => {
          if (funderList.externalId === params.externalId) {
            setCurrentFunderListUpdatedTime(new Date());
          }
        });
        onCloseModal();
      });
      res.notifyErrorIfPresent();
    });
  };

  const createFunderListWithSelectedFunders = () => {
    const funderListName: string = funderListNameRef.current.value;
    if (selectedFunders.length == 0 || funderListName.trim() == "") return;

    FunderListService.createListWithFunders(selectedFunders, funderListName).then((res) => {
      res.onSuccess((newFunderList) => {
        if (!newFunderList.externalId) {
          return;
        }
        setOpen(false);
        navigate(LazoPaths.FUNDER_LIST_DETAIL.replace(":externalId", newFunderList.externalId));
      });
      res.notifyErrorIfPresent();
    });
  };

  const onSave = () => {
    const newList = funderListNameRef.current ? funderListNameRef.current.value.trim() !== "" : false;
    const existingList = selectedFunderListsExternalIds.length > 0;

    if (newList) {
      createFunderListWithSelectedFunders();
    }
    if (existingList) {
      addSelectedFunderListToMultipleList();
    }
  };

  const isSelected = (funder: Funder) => {
    return Boolean(selectedFunders?.find((f) => f.uuid === funder.uuid));
  };

  const onClickAllFunderLists = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.checked) {
      setSelectedFunderListsExternalIds([]);
      return;
    }
    const externalsIds = funderLists.map((f) => f.externalId);

    setSelectedFunderListsExternalIds(externalsIds);
  };

  const selectAllFunders = (e: any) => {
    if (e.target.checked) {
      setSelectedFunders(funders);
      setAllChecked(true);
    } else {
      setSelectedFunders([]);
      setAllChecked(false);
    }
  };

  const funderListIsSelected = (externalId: string) => {
    return Boolean(selectedFunderListsExternalIds?.find((f) => f === externalId));
  };

  const addToSmartlistButton = () => {
    return (
      <span>
        <StyledButton disabled={!hasSmartListsCreation} onClick={() => setOpen(true)}>
          <FileDownload
            sx={{
              marginRight: "0.5rem",
              transform: "scale(1.2)",
            }}
          />
          Add to Smartlist
        </StyledButton>
      </span>
    );
  };

  return (
    <>
      {hasSmartListsCreation ? (
        addToSmartlistButton()
      ) : (
        <LazoTooltip title={getAddOnMessage(AddOnObjectKeyType.SMARTLISTS, "denied")} offset={[0, 50]}>
          {addToSmartlistButton()}
        </LazoTooltip>
      )}

      <LazoModal buttons={renderButtons()} open={open} handleClose={onCloseModal} title="Add to Smartlist">
        <Container>
          <Container>
            {funders.length > 1 && (
              <>
                <Typography marginBottom={"0.5rem"} className="subtitle">
                  Choose investors you would like to save
                </Typography>
                <ul>
                  <li key={"all"}>
                    <Container className="list-item">
                      <StyledInput
                        checked={allChecked}
                        onClick={(e) => {
                          selectAllFunders(e);
                        }}
                      />
                      <Typography>All</Typography>
                    </Container>
                  </li>
                  {funders.map((funder) => (
                    <li key={funder.uuid}>
                      <Container className="list-item">
                        <StyledInput checked={isSelected(funder)} onClick={() => onClickFunder(funder)} />
                        <Typography> {funder.entity.name}</Typography>
                      </Container>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {funders.length == 1 && (
              <>
                {funders.map((funder) => (
                  <Container className="funder-logo" key={funder.uuid}>
                    {/* @ts-ignore - I think maybe here we should use funder.entity.* instead of funder.* */}
                    <FunderImage funderImg={funder.logoUrl} />
                    {/* @ts-ignore - Idem */}
                    <Typography>{funder.name}</Typography>
                  </Container>
                ))}
              </>
            )}
          </Container>

          <Container
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "0.5rem",
              paddingTop: "1rem",
              alignItems: "flex-start",
            }}
          >
            <>
              {funderLists.length > 0 && (
                <>
                  <Divider />
                  <Typography className="subtitle">Save to Smartlist</Typography>
                  <ul>
                    <li key={"all"}>
                      <Container className="list-item">
                        <StyledInput
                          checked={selectedFunderListsExternalIds.length === funderLists.length}
                          onChange={(e) => onClickAllFunderLists(e)}
                        />
                        <Typography>All</Typography>
                      </Container>
                    </li>

                    {funderLists.map((funderList) => (
                      <li key={funderList.externalId}>
                        <Container className="list-item">
                          <StyledInput
                            checked={funderListIsSelected(funderList.externalId)}
                            onClick={() => {
                              onClickFunderList(funderList);
                            }}
                          />
                          <Typography>{funderList.name}</Typography>
                        </Container>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <CommonButton
                sx={{ marginTop: "0.5rem" }}
                onClick={() => setShowNameInput((prev) => !prev)}
                customVariant="light-purple-background"
              >
                Create new Smartlist
              </CommonButton>

              {showNameInput && (
                <Container sx={{ width: "100%", marginTop: "0.5rem" }}>
                  <Typography className="subtitle" paddingLeft={"8px"} color={Colors.LIGHT_GRAY_8A8E93}>
                    Name new Smartlist*
                  </Typography>
                  <InputField
                    defaultValue={lazoChatConversation?.title}
                    sx={{ width: "100%" }}
                    inputRef={funderListNameRef}
                    icon={<></>}
                  ></InputField>
                </Container>
              )}
            </>
          </Container>
        </Container>
      </LazoModal>
    </>
  );
};

const Container = styled(Box)(() => ({
  "& .subtitle": {
    fontSize: "0.75rem",
    fontWeight: "500",
  },
  "& ul": { padding: "0", margin: "0" },
  "& li": {
    listStyle: "none",
    "& p": {
      fontSize: "12px",
    },
  },
  "&.list-item": {
    display: "flex",
    columnGap: "0.5rem",
    alignItems: "center",
  },

  "&.funder-logo": {
    display: "flex",
    alignItems: "center",
    columnGap: "0.5rem",
    "& > img": {
      height: "2rem",
      width: "2rem",
    },
  },
}));

const StyledInput = styled(Checkbox)(() => ({
  "&.MuiButtonBase-root ": { padding: "0px" },
  "&.MuiCheckbox-root": {
    color: Colors.GREY,
    "& .MuiSvgIcon-root": {
      fontSize: "1.3rem",
    },
  },
  "&.Mui-checked": {
    color: Colors.LAZO_PURPLE,
  },
  "&.Mui-disabled": {
    color: Colors.GREY,
  },
}));

const StyledButton = styled(Button)(() => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "0.5rem",
  marginRight: "0.5rem",
  width: "fit-content",
  backgroundColor: Colors.BUTTON_GRAY,
  color: Colors.ELEGANT_BLACK,
  borderRadius: "0.5rem",
  textTransform: "none",
  ":hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
  },
  "& .MuiTypography-root": {
    fontWeight: "500",
  },
}));

export default AddInvestorToFunderListModal;
